import { createContext, useContext, type ReactNode } from "react";
import { dicts, type Dict } from "./dict";
import { DEFAULT_LOCALE, type Locale } from "./types";
import { translatePathToEn, translatePathToId } from "./paths";

interface LocaleCtx {
  locale: Locale;
  t: Dict;
  /** Build an internal href for the current locale. Pass a non-localised path like `/2026` or `/tools/cek-weton`. */
  href: (path: string) => string;
  /** Equivalent path in the *other* locale. */
  altHref: (path: string) => string;
  altLocale: Locale;
}

const Ctx = createContext<LocaleCtx | null>(null);

/**
 * Paths that exist ONLY in Indonesian. When called from an EN page,
 * `href()` must return the canonical ID URL (no /en prefix), otherwise
 * we generate dead /en/jadwal-sholat/... links that Googlebot indexes
 * as 404. See SC report: 519 affected pages, 5/2026.
 */
const ID_ONLY_PREFIXES = [
  "/jadwal-sholat",
  "/jadwal-imsak",
  "/jadwal-buka-puasa",
  "/hari/",
  "/hari",
  "/zodiak",
  "/harpitnas",
  "/kalkulator",
  // The Indonesian tools have no separate EN UI; the /en/tools/* aliases
  // only redirect. Generate links directly to the ID URL from EN pages
  // to avoid 307 redirect chains in internal links.
  "/tools/cek-weton",
  "/tools/kalkulator-hari",
  "/tools/hari-jadian",
  "/tools/hari-lahir",
  "/tools/hari-ldr",
  "/tools/hari-menikah",
  "/tools/hari-putus",
  "/tools/hari-sahabat",
];

function isIdOnlyPath(p: string): boolean {
  return ID_ONLY_PREFIXES.some(
    (pref) => p === pref || p.startsWith(pref + "/"),
  );
}

function buildHref(locale: Locale, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === "id") return clean;
  if (isIdOnlyPath(clean)) return clean;
  const translated = translatePathToEn(clean);
  if (clean === "/") return "/en";
  return `/en${translated}`;
}

/**
 * Strip the /en prefix off a current pathname AND translate any English
 * segments back to their canonical Indonesian form. Used by the language
 * switcher so that flipping locales lands on the equivalent ID URL.
 */
export function stripLocalePath(pathname: string): string {
  if (pathname === "/en" || pathname === "/en/") return "/";
  if (pathname.startsWith("/en/")) return translatePathToId(pathname.slice(3));
  return pathname;
}

export function LocaleProvider({ locale, children }: { locale?: Locale; children: ReactNode }) {
  const l: Locale = locale ?? DEFAULT_LOCALE;
  const value: LocaleCtx = {
    locale: l,
    t: dicts[l],
    href: (p) => buildHref(l, p),
    altHref: (p) => buildHref(l === "id" ? "en" : "id", p),
    altLocale: l === "id" ? "en" : "id",
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocale(): LocaleCtx {
  const v = useContext(Ctx);
  if (!v) {
    // SSR / default fallback to id
    const l = DEFAULT_LOCALE;
    return {
      locale: l,
      t: dicts[l],
      href: (p) => buildHref(l, p),
      altHref: (p) => buildHref("en", p),
      altLocale: "en",
    };
  }
  return v;
}